import React from 'react';

import { type SupportOrder } from '../../../../redux/types/SupportTypes';
import { formatDate } from '../../TgSessions/components/helpers';
import { formatMoney, orderAmount, requisiteLabel, shortId, statusLabel, statusTone } from '../helpers';
import styles from '../Support.module.css';

export type SupportColumn = { key: string; name: string };

export const SUPPORT_COLUMNS: SupportColumn[] = [
  { key: 'create_dt', name: 'Создана' },
  { key: 'id', name: 'ID заявки' },
  { key: 'merchant_order_id', name: 'ID у мерчанта' },
  { key: 'merchant', name: 'Мерчант' },
  { key: 'trader', name: 'Трейдер' },
  { key: 'requisite', name: 'Реквизит' },
  { key: 'amount', name: 'Сумма' },
  { key: 'status', name: 'Статус' },
  { key: 'disputes', name: 'Споры' },
  { key: 'modified', name: 'Изменена' },
];

type Props = {
  orders: SupportOrder[];
  visible: Record<string, boolean>;
  busy: boolean;
  onDispute: (order: SupportOrder) => void;
  onResolve: (order: SupportOrder, decision: 'approve' | 'reject') => void;
};

const cell = (order: SupportOrder, key: string): React.ReactNode => {
  switch (key) {
    case 'create_dt':
      return formatDate(order.create_dt);
    case 'id':
      return (
        <span className={styles.mono} title={order.id}>
          {shortId(order.id)}
        </span>
      );
    case 'merchant_order_id':
      return order.merchant_order_id || <span className={styles.muted}>—</span>;
    case 'merchant':
      return order.merchant || '—';
    case 'trader':
      return order.trader || <span className={styles.muted}>—</span>;
    case 'requisite':
      return requisiteLabel(order);
    case 'amount':
      return `${formatMoney(orderAmount(order))} ${order.currency_code_to}`;
    case 'status':
      return (
        <span className={`${styles.status} ${styles[statusTone(order.status)]}`} title={order.status_name}>
          {statusLabel(order.status)}
        </span>
      );
    case 'disputes':
      return order.disputes_count ? (
        <span title={order.last_dispute_comment || ''}>
          {order.disputes_count}
          {order.last_dispute_comment ? ` · ${order.last_dispute_comment.slice(0, 40)}` : ''}
        </span>
      ) : (
        <span className={styles.muted}>—</span>
      );
    case 'modified':
      return formatDate(order.last_modified_dt);
    default:
      return null;
  }
};

// The actions column is always rendered: it is not part of the column picker
export const SupportTable: React.FC<Props> = ({ orders, visible, busy, onDispute, onResolve }) => {
  const columns = SUPPORT_COLUMNS.filter((column) => visible[column.key] !== false);
  if (orders.length === 0) {
    return <div className={styles.empty}>Заявок по этим условиям нет.</div>;
  }
  return (
    <div className={styles.tableWrap}>
      <table className={styles.table}>
        <thead>
          <tr>
            {columns.map((column) => (
              <th key={column.key}>{column.name}</th>
            ))}
            <th>Действия</th>
          </tr>
        </thead>
        <tbody>
          {orders.map((order) => (
            <tr key={order.id}>
              {columns.map((column) => (
                <td key={column.key}>{cell(order, column.key)}</td>
              ))}
              <td>
                <div className={styles.actions}>
                  {order.can_open_dispute && (
                    <button type='button' className={styles.actionButton} disabled={busy} onClick={() => onDispute(order)}>
                      Открыть спор
                    </button>
                  )}
                  {order.can_resolve_dispute && (
                    <>
                      <button type='button' className={styles.actionButton} disabled={busy} onClick={() => onResolve(order, 'approve')}>
                        Подтвердить спор
                      </button>
                      <button
                        type='button'
                        className={`${styles.actionButton} ${styles.danger}`}
                        disabled={busy}
                        onClick={() => onResolve(order, 'reject')}
                      >
                        Отклонить спор
                      </button>
                    </>
                  )}
                  {!order.can_open_dispute && !order.can_resolve_dispute && <span className={styles.muted}>—</span>}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
